"use client";

import { useEffect, useState } from "react";
import { ChevronDown, List } from "lucide-react";
import type { ArticleBlock } from "@/lib/types";
import { cn } from "@/lib/utils";

type Props = {
  blocks: ArticleBlock[];
};

type Item = { id: string; text: string };

export default function TableOfContents({ blocks }: Props) {
  // ArticleBody と同じ採番（heading 出現順）で id を生成
  const items: Item[] = [];
  let idx = -1;
  for (const b of blocks) {
    if (b.type === "heading") {
      idx += 1;
      items.push({ id: `section-${idx}`, text: b.text });
    }
  }

  const [activeId, setActiveId] = useState<string>(items[0]?.id ?? "");
  const [open, setOpen] = useState(false);

  useEffect(() => {
    if (items.length === 0) return;
    const headings = items
      .map((it) => document.getElementById(it.id))
      .filter((el): el is HTMLElement => el !== null);

    const observer = new IntersectionObserver(
      (entries) => {
        const visible = entries
          .filter((e) => e.isIntersecting)
          .sort((a, b) => a.boundingClientRect.top - b.boundingClientRect.top);
        if (visible[0]) setActiveId(visible[0].target.id);
      },
      { rootMargin: "-20% 0px -65% 0px", threshold: 0 }
    );

    headings.forEach((h) => observer.observe(h));
    return () => observer.disconnect();
    // items は blocks から導出。blocks 不変前提
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [blocks]);

  if (items.length === 0) return null;

  return (
    <nav
      className="mb-12 rounded-xl2 border border-line bg-mist/60 p-5 md:p-6"
      aria-label="目次"
    >
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="flex w-full items-center justify-between md:cursor-default"
        aria-expanded={open}
      >
        <span className="flex items-baseline gap-2">
          <List className="h-4 w-4 self-center text-navy-700" aria-hidden />
          <span className="font-en text-sm font-bold uppercase tracking-[0.14em] text-navy-900">
            Contents
          </span>
          <span className="text-xs font-medium text-muted">目次</span>
        </span>
        <ChevronDown
          className={cn(
            "h-5 w-5 text-navy-400 transition-transform md:hidden",
            open && "rotate-180"
          )}
          aria-hidden
        />
      </button>

      <ol className={cn("mt-4 space-y-1", open ? "block" : "hidden md:block")}>
        {items.map((it, i) => (
          <li key={it.id}>
            <a
              href={`#${it.id}`}
              className={cn(
                "flex gap-3 rounded-md px-2 py-2 text-sm leading-snug transition-colors",
                activeId === it.id
                  ? "bg-white font-semibold text-navy-700 shadow-card"
                  : "text-muted hover:text-navy-700"
              )}
            >
              <span className="font-en text-xs font-bold text-navy-300">
                {String(i + 1).padStart(2, "0")}
              </span>
              <span>{it.text}</span>
            </a>
          </li>
        ))}
      </ol>
    </nav>
  );
}
